import { computed, inject, Injectable, Signal, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map, startWith } from 'rxjs/operators';
import { Subscription } from 'rxjs';

import { adminPermissions, clientPermissions, iSideBarMenuItem } from '@interfaces/sidebar.interface';
import { iThemeTypes } from '@interfaces/themes.interface';
import { MenuSystemService } from '@shared/menu/menu-system.service';
import { environment } from 'environments/environment';
import { EnterpriseService } from './enterprise.service';
import { ClientProviderService } from './client-provider.service';
import { FormGroupService } from './form-group.service';
import { IScheme } from '@interfaces/schemes.interface';
@Injectable({
  providedIn: 'root'
})
export class SidebarService {
  private menuSystemService = inject(MenuSystemService);
  private enterpriseService = inject(EnterpriseService);
  private clientProviderService = inject(ClientProviderService);
  private formGroupService = inject(FormGroupService);
  private router = inject(Router);

  private toggleSideBar = signal<boolean>(false);
  private menus = signal<iSideBarMenuItem[] | null>(null);
  private menuType: 'ADMIN' | 'SUPPORT' | 'CLIENT' | 'PUBLIC' | null = null;
  private menuContextVersion = 0;

  private currentUrl = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(e => e.urlAfterRedirects.split('?')[0].split('#')[0]),
      startWith(this.router.url.split('?')[0].split('#')[0])
    ),
    { initialValue: this.router.url.split('?')[0].split('#')[0] }
  );

  isEditor = computed(() => {
    const menus = this.menus();
    if (!menus) return true;
    const url = this.currentUrl() ?? '';
    const permType = this.findPermissionTypeForUrl(url, menus);
    return permType !== 'VISUALIZA';
  });

  private findPermissionTypeForUrl(url: string, items: iSideBarMenuItem[]): string | null {
    let bestMatchLength = 0;
    let bestPermType: string | null = null;

    const search = (items: iSideBarMenuItem[]) => {
      for (const item of items) {
        if (item.route && !item.external) {
          const cleanRoute = item.route.split('?')[0].split('#')[0];
          if (cleanRoute.length > 1 && url.startsWith(cleanRoute) && item.permissionType) {
            if (cleanRoute.length > bestMatchLength) {
              bestMatchLength = cleanRoute.length;
              bestPermType = item.permissionType;
            }
          }
        }
        if (item.subRoute?.length) {
          search(item.subRoute);
        }
      }
    };

    search(items);
    return bestPermType;
  }

  customerId: Signal<string | null> = this.enterpriseService.getCustomerId();
  private clientPermissions = clientPermissions;
  private adminPermissions = adminPermissions;

  token: string = sessionStorage.getItem('token') as string;
  systemType = sessionStorage.getItem('systemType') as iThemeTypes;
  schemesList = this.enterpriseService.getSchemesList();

  customerIdSubscription!: Subscription;

  getToggleSideBar(): Signal<boolean> {
    return this.toggleSideBar.asReadonly();
  }

  setToggleSideBar(value: boolean) {
    this.toggleSideBar.set(value);
  }

  setMenu(v: iSideBarMenuItem[]) {
    this.menus.set(v);
  }

  reset() {
    this.toggleSideBar.set(false);
    this.menus.set(null);
    this.menuType = null;
    this.menuContextVersion++;
    this.menuSystemService.clearHomePage();
    this.token = sessionStorage.getItem('token') as string;
    this.systemType = sessionStorage.getItem('systemType') as iThemeTypes;
  }

  getMenu(): Signal<iSideBarMenuItem[] | null> {
    if (!this.menus()) {
      // this.getMenuInfo();
    }
    return this.menus.asReadonly();
  }

  getMenuInfo(type: 'ADMIN' | 'SUPPORT' | 'CLIENT' | 'PUBLIC') {
    const token = sessionStorage.getItem('token') as string;
    if (this.menuType !== type || this.token !== token) {
      this.menus.set(null);
      this.menuType = type;
      this.menuContextVersion++;
      this.menuSystemService.clearHomePage();
    }
    this.token = token;
    this.systemType = sessionStorage.getItem('systemType') as iThemeTypes;

    switch (type) {
      case 'ADMIN':
        this.getMenuInfoAdminPanel();
        break;
      case 'CLIENT':
        this.getMenuInfoClientPanel();
        break;
      case 'SUPPORT':
        this.setMenu([
          {
            code: 'SUPPORT',
            legend: 'SIDEBAR.SUPPORT',
            route: '/support-panel',
            icon: 'SUPPORT',
          },

        ]);
        break;
      case 'PUBLIC':
        this.setMenu([
          {
            code: 'REQUEST-QUOTE',
            legend: 'SIDEBAR.REQUEST-QUOTE',
            route: '/public-panel/request-quote',
            icon: 'CERTIFICATIONS',
          },
        ]);
        break;
    }
  }

  getMenuInfoAdminPanel() {
    const userItem = sessionStorage.getItem('user');
    const user = userItem ? JSON.parse(userItem) : null;

    if (!this.menus()) {
      if (!user.menuId)
        return this.getSchemesListAdmin();
      const contextVersion = this.menuContextVersion;
      this.menuSystemService.find(user.menuId).subscribe({
        next: res => {
          if (contextVersion !== this.menuContextVersion) {
            return;
          }
          const homePage = this.menuSystemService.getHomePage();
          this.setMenu(res.permissions ?? []);
          res.permissions.forEach(permission => {
            if (permission.homePage) {
              if (!homePage()) {
                setTimeout(() => {
                  this.router.navigate([permission.route]);
                }, 500);
              }
              this.menuSystemService.setHomePage(permission.route);
              return;
            }
            permission.subRoute.forEach(subPermission => {
              if (subPermission.homePage) {
                if (!homePage() && this.isPanelRootRoute()) {
                  setTimeout(() => {
                    this.router.navigate([permission.route]);
                  }, 500);
                }
                this.menuSystemService.setHomePage(subPermission.route);
                return;
              }
            });
          });
        },
        error: () => {
          if (contextVersion !== this.menuContextVersion) {
            return;
          }
          this.getSchemesListAdmin();
          this.setMenu(this.adminPermissions);
        },
        complete: () => {
          if (contextVersion !== this.menuContextVersion) {
            return;
          }
          this.getSchemesListAdmin();
        }
      });
    }

    if (
      this.systemType === 'MINERVA' ||
      this.systemType === 'CARGILL' ||
      this.systemType === 'ACELEN' ||
      this.systemType === 'IRANCHO' ||
      this.systemType === 'WALMART' ||
      this.systemType === 'INTERSTATE_MEAT_DISTRIBUTORS' ||
      this.systemType === 'SAFETRACE' ||
      this.systemType === 'GRAOSDOPARA'
    ) {
      this.adminPermissions = this.getBaseAdminPermissions(true);
    } else if (this.systemType === 'THOMAS_FOODS') {
      this.adminPermissions = this.getBaseAdminPermissions(false);
    } else if (this.systemType === 'ASSAI') {
      this.adminPermissions = this.getBaseAdminPermissions(true, true);
    }
  }

  private isPanelRootRoute(): boolean {
    const currentUrl = this.router.url.split('?')[0];
    return currentUrl === '/panel' || currentUrl === '/panel/' || currentUrl == '/client-panel' || currentUrl === '/client-panel/';
  }

  getMenuInfoClientPanel() {
    const userItem = sessionStorage.getItem('user');
    const user = userItem ? JSON.parse(userItem) : null;

    if (!user.menuId)
      return this.getSchemesListClient();
    const contextVersion = this.menuContextVersion;
    this.menuSystemService.find(user.menuId).subscribe({
      next: res => {
        if (contextVersion !== this.menuContextVersion) {
          return;
        }
        const homePage = this.menuSystemService.getHomePage();
        if (this.menus() !== res.permissions) {
          this.setMenu(res.permissions as iSideBarMenuItem[]);
        }
        res.permissions.forEach(permission => {
          if (permission.homePage) {
            if (!homePage() && homePage() !== permission.route && this.isPanelRootRoute()) {
              setTimeout(() => {
                this.router.navigate([permission.route]);
              }, 500);
            }
            this.menuSystemService.setHomePage(permission.route);
            return;
          }
          permission.subRoute.forEach(subPermission => {
            if (subPermission.homePage) {
                if (!homePage() && this.isPanelRootRoute()) {
                setTimeout(() => {
                  this.router.navigate([permission.route]);
                }, 500);
              }
              this.menuSystemService.setHomePage(subPermission.route);
              return;
            }
          });
        });
      },
      error: () => {
        if (contextVersion !== this.menuContextVersion) {
          return;
        }
        this.getSchemesListClient();
        this.setMenu(this.clientPermissions as iSideBarMenuItem[]);
      },
      complete: () => {
        if (contextVersion !== this.menuContextVersion) {
          return;
        }
        this.getSchemesListClient();
      }
    });
  }

  getSchemesListClient() {
    const userItem = sessionStorage.getItem('user');
    const user = userItem ? JSON.parse(userItem) : null;
    const token = sessionStorage.getItem('token') as string;
    if (
      this.systemType === 'MINERVA' ||
      this.systemType === 'CARGILL' ||
      this.systemType === 'ACELEN' ||
      this.systemType === 'IRANCHO' ||
      this.systemType === 'WALMART' ||
      this.systemType === 'INTERSTATE_MEAT_DISTRIBUTORS' ||
      this.systemType === 'ASSAI' ||
      this.systemType === 'SAFETRACE' ||
      this.systemType === 'GRAOSDOPARA'
    ) {
      if (this.systemType === 'ACELEN') {
        this.clientPermissions = [
          {
            code: 'INSTRUCTIONS',
            legend: 'SIDEBAR.INSTRUCTIONS',
            route: '/client-panel/instructions',
            icon: 'INSTRUCTIONS',
          },
          {
            code: 'DASHBOARD',
            legend: 'SIDEBAR.DASHBOARD',
            route: '/client-panel/dashboard',
            icon: 'DASHBOARD',
          },
          {
            code: 'OPERATION-PLAN',
            legend: 'SIDEBAR.OPERATION-PLAN.TITLE',
            route: '/client-panel/operation-plan',
            icon: 'OPERATION-PLAN',
            subRoute: [
              {
                code: 'SERVICE-ORDER',
                legend: 'SIDEBAR.SERVICE-ORDER',
                route: '/client-panel/operation-plan/service-order',
                icon: 'SERVICE-ORDER'
              },
              {
                code: 'FARMING',
                legend: 'SIDEBAR.OPERATION.FARMING.TITLE',
                route: '/client-panel/operation-plan/farming/',
                subRoute: [
                  {
                    code: 'PRE-PLANTING-OPERATION',
                    legend: 'SIDEBAR.OPERATION.FARMING.PRE-PLANTING-OPERATION.TITLE',
                    route: '/client-panel/operation-plan/farming/pre-planting-operation',
                    subRoute: [
                      {
                        code: 'AREA-CLEANING',
                        legend: 'SIDEBAR.OPERATION.FARMING.PRE-PLANTING-OPERATION.AREA-CLEANING',
                        route: '/client-panel/operation-plan/farming/pre-planting-operation/area-cleaning/67ff8c143f77c6087f1be194',
                      },
                      {
                        code: 'PEST-MANAGEMENT',
                        legend: 'SIDEBAR.OPERATION.FARMING.PRE-PLANTING-OPERATION.PEST-MANAGEMENT',
                        route: '/client-panel/operation-plan/farming/pre-planting-operation/pest-management/67ff8d793f77c6087f1be1aa',
                      },
                      {
                        code: 'SOIL-CORRECTION',
                        legend: 'SIDEBAR.OPERATION.FARMING.PRE-PLANTING-OPERATION.SOIL-CORRECTION',
                        route: '/client-panel/operation-plan/farming/pre-planting-operation/soil-correction/67ff8ec83f77c6087f1be1c0',
                      },
                      {
                        code: 'DIGGING-OF-GRAVES',
                        legend: 'SIDEBAR.OPERATION.FARMING.PRE-PLANTING-OPERATION.DIGGING-OF-GRAVES',
                        route: '/client-panel/operation-plan/farming/pre-planting-operation/digging-of-graves/67ff90f43f77c6087f1be1d2',
                      },
                      {
                        code: 'IRRIGATION',
                        legend: 'SIDEBAR.OPERATION.FARMING.PRE-PLANTING-OPERATION.IRRIGATION',
                        route: '/client-panel/operation-plan/farming/pre-planting-operation/irrigation/67ff90443f77c6087f1be1d1',
                      },
                    ],
                  },
                  {
                    code: 'PLANTING-PHASE',
                    legend: 'SIDEBAR.OPERATION.FARMING.PLANTING-PHASE.TITLE',
                    route: '/client-panel/operation-plan/farming/planting-phase',
                    subRoute: [
                      {
                        code: 'FOUNDATION-FERTILIZATION',
                        legend: 'SIDEBAR.OPERATION.FARMING.PLANTING-PHASE.FOUNDATION-FERTILIZATION',
                        route: '/client-panel/operation-plan/farming/planting-phase/foundation-fertilization/67ff93363f77c6087f1be1e5',
                      },
                      {
                        code: 'PLANTING',
                        legend: 'SIDEBAR.OPERATION.FARMING.PLANTING-PHASE.PLANTING',
                        route: '/client-panel/operation-plan/farming/planting-phase/planting/67ff92a03f77c6087f1be1e4',
                      },
                    ],
                  },
                  {
                    code: 'POST-PLANT-PHASE',
                    legend: 'SIDEBAR.OPERATION.FARMING.POST-PLANT-PHASE.TITLE',
                    route: '/client-panel/operation-plan/farming/post-planting-phase',
                    subRoute: [
                      {
                        code: 'FERTILIZATION-WITH-IRRIGATION',
                        legend: 'SIDEBAR.OPERATION.FARMING.POST-PLANT-PHASE.FERTILIZATION-WITH-IRRIGATION',
                        route: '/client-panel/operation-plan/farming/post-planting-phase/fertilization-with-irrigation/67ff94fe3f77c6087f1be1e7',
                      },
                      {
                        code: 'PHYTOSANITARY-CONTROL',
                        legend: 'SIDEBAR.OPERATION.FARMING.POST-PLANT-PHASE.PHYTOSANITARY-CONTROL',
                        route: '/client-panel/operation-plan/farming/post-planting-phase/phytosanitary-control/67ff956b3f77c6087f1be1e8',
                      },
                      {
                        code: 'CHEMICAL-CROWNING',
                        legend: 'SIDEBAR.OPERATION.FARMING.POST-PLANT-PHASE.CHEMICAL-CROWNING',
                        route: '/client-panel/operation-plan/farming/post-planting-phase/chemical-crowning/67ff95cc3f77c6087f1be1e9',
                      },
                      {
                        code: '"MECHANIZED-PHOSPHATING',
                        legend: 'SIDEBAR.OPERATION.FARMING.POST-PLANT-PHASE.MECHANIZED-PHOSPHATING',
                        route: '/client-panel/operation-plan/farming/post-planting-phase/mechanized-phosphating/67ff96223f77c6087f1be1ea',
                      },
                    ]
                  },
                  {
                    code: 'POST-PLANTING-IRRIGATION',
                    legend: 'SIDEBAR.OPERATION.FARMING.POST-PLANTING-IRRIGATION.TITLE',
                    route: '/client-panel/operation-plan/farming/post-planting-irrigation',
                    subRoute: [
                      {
                        code: 'IRRIGATION',
                        legend: 'SIDEBAR.OPERATION.FARMING.POST-PLANTING-IRRIGATION.IRRIGATION',
                        route: '/client-panel/operation-plan/farming/post-planting-irrigation/irrigation/67ff947a3f77c6087f1be1e6',
                      }
                    ]
                  },
                  {
                    code: 'INBOUND-LOGISTIC-OPERATION',
                    legend: 'SIDEBAR.OPERATION.FARMING.INBOUND-LOGISTIC-OPERATION.TITLE',
                    route: '/client-panel/operation-plan/farming/inbound-logistic-operation',
                    subRoute: [
                      {
                        code: 'TRANSPORT-OF-SEEDLINGS',
                        legend: 'SIDEBAR.OPERATION.FARMING.INBOUND-LOGISTIC-OPERATION.TRANSPORT-OF-SEEDLINGS',
                        route: '/client-panel/operation-plan/farming/inbound-logistic-operation/transport-of-seedlings/67ff92453f77c6087f1be1e3',
                      }
                    ]
                  },
                  {
                    code: 'HARVEST',
                    legend: 'SIDEBAR.OPERATION.FARMING.HARVEST.TITLE',
                    route: '/client-panel/operation-plan/farming/harvest',
                    subRoute: [
                      {
                        code: 'FRESH-FRUIT-HARVESTING',
                        legend: 'SIDEBAR.OPERATION.FARMING.HARVEST.FRESH-FRUIT-HARVESTING',
                        route: '/client-panel/operation-plan/farming/harvest/fresh-fruit-harvesting/67ff96803f77c6087f1be1eb',
                      },
                      {
                        code: 'MECHANIZED-LOADING',
                        legend: 'SIDEBAR.OPERATION.FARMING.HARVEST.MECHANIZED-LOADING',
                        route: '/client-panel/operation-plan/farming/harvest/mechanized-loading/67ff974b3f77c6087f1be1fa',
                      },
                      {
                        code: 'TRANSPORT-OF-FRESH-FRUIT',
                        legend: 'SIDEBAR.OPERATION.FARMING.HARVEST.TRANSPORT-OF-FRESH-FRUIT',
                        route: '/client-panel/operation-plan/farming/harvest/transport-of-fresh-fruit/67ff97a93f77c6087f1be1fb',
                      }
                    ]
                  },
                ]
              },

            ],
          },
          {
            code: 'OPERATION',
            legend: 'SIDEBAR.OPERATION.TITLE',
            route: '/client-panel/operation',
            icon: 'OPERATION-PLAN',
            subRoute: [
              {
                code: 'FARMING',
                legend: 'SIDEBAR.OPERATION.FARMING.TITLE',
                route: '/client-panel/operation/farming/',
                subRoute: [
                  {
                    code: 'PRE-PLANTING-OPERATION',
                    legend: 'SIDEBAR.OPERATION.FARMING.PRE-PLANTING-OPERATION.TITLE',
                    route: '/client-panel/operation/farming/pre-planting-operation',
                    subRoute: [
                      {
                        code: 'AREA-CLEANING',
                        legend: 'SIDEBAR.OPERATION.FARMING.PRE-PLANTING-OPERATION.AREA-CLEANING',
                        route: '/client-panel/operation/farming/pre-planting-operation/area-cleaning/67fd726b7f73f4020c54101a',
                      },
                      {
                        code: 'PEST-MANAGEMENT',
                        legend: 'SIDEBAR.OPERATION.FARMING.PRE-PLANTING-OPERATION.PEST-MANAGEMENT',
                        route: '/client-panel/operation/farming/pre-planting-operation/pest-management/67fd72b47f73f4020c54101b',
                      },
                      {
                        code: 'SOIL-CORRECTION',
                        legend: 'SIDEBAR.OPERATION.FARMING.PRE-PLANTING-OPERATION.SOIL-CORRECTION',
                        route: '/client-panel/operation/farming/pre-planting-operation/soil-correction/67fd75d07f73f4020c541025',
                      },
                      {
                        code: 'DIGGING-OF-GRAVES',
                        legend: 'SIDEBAR.OPERATION.FARMING.PRE-PLANTING-OPERATION.DIGGING-OF-GRAVES',
                        route: '/client-panel/operation/farming/pre-planting-operation/digging-of-graves/67fd732b7f73f4020c54101d',
                      },
                      {
                        code: 'IRRIGATION',
                        legend: 'SIDEBAR.OPERATION.FARMING.PRE-PLANTING-OPERATION.IRRIGATION',
                        route: '/client-panel/operation/farming/pre-planting-operation/irrigation/67fd72e57f73f4020c54101c',
                      },
                    ],
                  },
                  {
                    code: 'PLANTING-PHASE',
                    legend: 'SIDEBAR.OPERATION.FARMING.PLANTING-PHASE.TITLE',
                    route: '/client-panel/operation/farming/planting-phase',
                    subRoute: [
                      {
                        code: 'FOUNDATION-FERTILIZATION',
                        legend: 'SIDEBAR.OPERATION.FARMING.PLANTING-PHASE.FOUNDATION-FERTILIZATION',
                        route: '/client-panel/operation/farming/planting-phase/foundation-fertilization/67fd73e17f73f4020c541020',
                      },
                      {
                        code: 'PLANTING',
                        legend: 'SIDEBAR.OPERATION.FARMING.PLANTING-PHASE.PLANTING',
                        route: '/client-panel/operation/farming/planting-phase/planting/67fd73a77f73f4020c54101f',
                      },
                    ],
                  },
                  {
                    code: 'POST-PLANT-PHASE',
                    legend: 'SIDEBAR.OPERATION.FARMING.POST-PLANT-PHASE.TITLE',
                    route: '/client-panel/operation/farming/post-planting-phase',
                    subRoute: [
                      {
                        code: 'FERTILIZATION-WITH-IRRIGATION',
                        legend: 'SIDEBAR.OPERATION.FARMING.POST-PLANT-PHASE.FERTILIZATION-WITH-IRRIGATION',
                        route: '/client-panel/operation/farming/post-planting-phase/fertilization-with-irrigation/67fd761e7f73f4020c541026',
                      },
                      {
                        code: 'PHYTOSANITARY-CONTROL',
                        legend: 'SIDEBAR.OPERATION.FARMING.POST-PLANT-PHASE.PHYTOSANITARY-CONTROL',
                        route: '/client-panel/operation/farming/post-planting-phase/phytosanitary-control/67fd76577f73f4020c541027',
                      },
                      {
                        code: 'CHEMICAL-CROWNING',
                        legend: 'SIDEBAR.OPERATION.FARMING.POST-PLANT-PHASE.CHEMICAL-CROWNING',
                        route: '/client-panel/operation/farming/post-planting-phase/chemical-crowning/67fd768d7f73f4020c541028',
                      },
                      {
                        code: '"MECHANIZED-PHOSPHATING',
                        legend: 'SIDEBAR.OPERATION.FARMING.POST-PLANT-PHASE.MECHANIZED-PHOSPHATING',
                        route: '/client-panel/operation/farming/post-planting-phase/mechanized-phosphating/67fd76c97f73f4020c541029',
                      },
                    ]
                  },
                  {
                    code: 'POST-PLANTING-IRRIGATION',
                    legend: 'SIDEBAR.OPERATION.FARMING.POST-PLANTING-IRRIGATION.TITLE',
                    route: '/client-panel/operation/farming/post-planting-irrigation',
                    subRoute: [
                      {
                        code: 'IRRIGATION',
                        legend: 'SIDEBAR.OPERATION.FARMING.POST-PLANTING-IRRIGATION.IRRIGATION',
                        route: '/client-panel/operation/farming/post-planting-irrigation/irrigation/67fd74417f73f4020c541021',
                      }
                    ]
                  },
                  {
                    code: 'INBOUND-LOGISTIC-OPERATION',
                    legend: 'SIDEBAR.OPERATION.FARMING.INBOUND-LOGISTIC-OPERATION.TITLE',
                    route: '/client-panel/operation/farming/imbound-logistic-operation',
                    subRoute: [
                      {
                        code: 'TRANSPORT-OF-SEEDLINGS',
                        legend: 'SIDEBAR.OPERATION.FARMING.INBOUND-LOGISTIC-OPERATION.TRANSPORT-OF-SEEDLINGS',
                        route: '/client-panel/operation/farming/imbound-logistic-operation/transport-of-seedlings/67fd736b7f73f4020c54101e',
                      }
                    ]
                  },
                  {
                    code: 'HARVEST',
                    legend: 'SIDEBAR.OPERATION.FARMING.HARVEST.TITLE',
                    route: '/client-panel/operation/farming/harvest',
                    subRoute: [
                      {
                        code: 'FRESH-FRUIT-HARVESTING',
                        legend: 'SIDEBAR.OPERATION.FARMING.HARVEST.FRESH-FRUIT-HARVESTING',
                        route: '/client-panel/operation/farming/harvest/fresh-fruit-harvesting/67fd747e7f73f4020c541022',
                      },
                      {
                        code: 'MECHANIZED-LOADING',
                        legend: 'SIDEBAR.OPERATION.FARMING.HARVEST.MECHANIZED-LOADING',
                        route: '/client-panel/operation/farming/harvest/mechanized-loading/67fd74b47f73f4020c541023',
                      },
                      {
                        code: 'TRANSPORT-OF-FRESH-FRUIT',
                        legend: 'SIDEBAR.OPERATION.FARMING.HARVEST.TRANSPORT-OF-FRESH-FRUIT',
                        route: '/client-panel/operation/farming/harvest/transport-of-fresh-fruit/67fd74e67f73f4020c541024',
                      }
                    ]
                  },
                ]
              },
              {
                code: 'MILLING',
                legend: 'SIDEBAR.OPERATION.MILLING.TITLE',
                route: '/client-panel/operation/milling/',
                subRoute: [
                  {
                    code: 'RAW-MATERIAL-RECEIVING',
                    legend: 'SIDEBAR.OPERATION.MILLING.RAW-MATERIAL-RECEIVING',
                    route: '/client-panel/operation/milling/milling/raw-material-receiving/681c8fd751731c2f6dad92e0',
                  },
                  {
                    code: 'SEPARATION-OF-THE-FRUIT-AND-BUNCH',
                    legend: 'SIDEBAR.OPERATION.MILLING.SEPARATION-OF-THE-FRUIT-AND-BUNCH',
                    route: '/client-panel/operation/milling/milling/separation-of-the-fruit-and-bunch/6800e7437697f67adef41332',
                  },
                  {
                    code: 'FRUIT-DRYER',
                    legend: 'SIDEBAR.OPERATION.MILLING.FRUIT-DRYER',
                    route: '/client-panel/operation/milling/milling/fruit-dryer/6819ffca9d779b4ac9d3e2b4',
                  },
                  {
                    code: 'FRUIT-PEELER',
                    legend: 'SIDEBAR.OPERATION.MILLING.FRUIT-PEELER',
                    route: '/client-panel/operation/milling/milling/fruit-peeler/6819ffa89d779b4ac9d3e2b3',
                  },
                  {
                    code: 'FRUIT-PULP-PRESS',
                    legend: 'SIDEBAR.OPERATION.MILLING.FRUIT-PULP-PRESS',
                    route: '/client-panel/operation/milling/milling/fruit-pulp-press/6819ffef9d779b4ac9d3e2b5',
                  },
                  {
                    code: 'OIL-STORAGE',
                    legend: 'SIDEBAR.OPERATION.MILLING.OIL-STORAGE',
                    route: '/client-panel/operation/milling/milling/oil-storage/6800e6fa7697f67adef41330',
                  },
                  {
                    code: 'OIL-TRANSPORT',
                    legend: 'SIDEBAR.OPERATION.MILLING.OIL-TRANSPORT',
                    route: '/client-panel/operation/milling/milling/oil-transport/6800e7c67697f67adef41333',
                  },
                ]
              },
              {
                code: 'REFINERY',
                legend: 'SIDEBAR.OPERATION.REFINERY.TITLE',
                route: '/client-panel/operation/refinery/',
                subRoute: [
                  {
                    code: 'OIL-RECEIVING',
                    legend: 'SIDEBAR.OPERATION.REFINERY.OIL-RECEIVING',
                    route: '/client-panel/operation/refinery/refinery/oil-receiving/6800f26912d4f055b9ac00d8',
                  },
                  {
                    code: 'FEEDING-OIL-STORAGE',
                    legend: 'SIDEBAR.OPERATION.REFINERY.FEEDING-OIL-STORAGE',
                    route: '/client-panel/operation/refinery/refinery/feeding-oil-storage/6800ecd67697f67adef41349',
                  },
                  {
                    code: 'DEGUMMING',
                    legend: 'SIDEBAR.OPERATION.REFINERY.DEGUMMING',
                    route: '/client-panel/operation/refinery/refinery/degumming/6800f10612d4f055b9ac00ca',
                  },
                  {
                    code: 'OIL-WASHING',
                    legend: 'SIDEBAR.OPERATION.REFINERY.OIL-WASHING',
                    route: '/client-panel/operation/refinery/refinery/oil-washing/681a005e9d779b4ac9d3e2b6',
                  },
                  {
                    code: 'HYDROTREATMENT',
                    legend: 'SIDEBAR.OPERATION.REFINERY.HYDROTREATMENT',
                    route: '/client-panel/operation/refinery/refinery/hydrotreatment/681a00879d779b4ac9d3e2b7',
                  },
                  {
                    code: 'FRACTIONATION',
                    legend: 'SIDEBAR.OPERATION.REFINERY.FRACTIONATION',
                    route: '/client-panel/operation/refinery/refinery/fractionation/681a00b49d779b4ac9d3e2b8',
                  },
                  {
                    code: 'BIOFUEL-STORAGE-IN-TANKS',
                    legend: 'SIDEBAR.OPERATION.REFINERY.BIOFUEL-STORAGE-IN-TANKS',
                    route: '/client-panel/operation/refinery/refinery/biofuel-storage-in-tanks/681a01029d779b4ac9d3e2b9',
                  },
                ]
              },
              {
                code: 'OUTBOUND-LOGISTCS',
                legend: 'SIDEBAR.OPERATION.OUTBOUND-LOGISTCS.TITLE',
                route: '/client-panel/operation/outbound-logistcs/',
                subRoute: [
                  {
                    code: 'TRANSFER-OF-PRODUCT-FROM-STORAGE-TANKS-BY-PIPELAND',
                    legend: 'SIDEBAR.OPERATION.OUTBOUND-LOGISTCS.TRANSFER-OF-PRODUCT-FROM-STORAGE-TANKS-BY-PIPELAND',
                    route: '/client-panel/operation/outbound-logistcs/outbound-logistcs/transfer-of-product-from-storage-tanks-by-pipeland/6800f57d12d4f055b9ac00f4',
                  },
                  {
                    code: 'WATER-TRANSPORT',
                    legend: 'SIDEBAR.OPERATION.OUTBOUND-LOGISTCS.WATER-TRANSPORT',
                    route: '/client-panel/operation/outbound-logistcs/outbound-logistcs/water-transport/681a01829d779b4ac9d3e2ba',
                  },
                ]
              },
            ],
          },
          {
            code: 'REGISTER',
            legend: 'Cadastro',
            route: '/client-panel/register',
            icon: 'REGISTER',
            subRoute: [
              {
                code: 'MY-DATA',
                legend: 'SIDEBAR.MY-DATA',
                route: '/client-panel/register/my-data',
                icon: 'MY-DATA'
              },
              {
                code: 'PROVIDERS',
                legend: 'SIDEBAR.PROVIDERS',
                route: '/client-panel/register/providers',
                icon: 'CLIENTS',
              },
              {
                code: 'PRODUCTS',
                legend: 'SIDEBAR.PRODUCTS',
                route: '/client-panel/register/products',
                icon: 'PRODUCTS'
              },
              {
                code: 'EMISSION-FACTOR',
                legend: 'Fator de Emissão',
                route: '/client-panel/register/emission-factor',
                icon: 'EMISSION-FACTOR'
              },
              {
                code: 'MACHINE',
                legend: 'SIDEBAR.MACHINE',
                route: '/client-panel/register/machine',
                icon: 'MACHINE'
              },
              {
                code: 'MACROPROCESS',
                legend: 'Macroprocessos',
                route: '/client-panel/register/macroprocess',
                icon: 'MACROPROCESS',
                subRoute: [
                  // {
                  //   code: 'HOMOLOGATION-PROCESS',
                  //   legend: 'Listagem',
                  //   route: '/client-panel/register/macroprocess/homologation-process',
                  //   icon: 'MAIL'
                  // },
                  {
                    code: 'ASSOCIATE',
                    legend: 'SIDEBAR.ASSOCIATE',
                    route: '/client-panel/register/macroprocess/associate',
                    icon: 'ASSOCIATE'
                  },
                  {
                    code: 'CHECKLISTS',
                    legend: 'SIDEBAR.CHECKLISTS',
                    route: '/client-panel/register/macroprocess/checklists',
                    icon: 'CHECKLISTS',
                  }]
              },
            ],
          },
          {
            code: 'NOTES',
            legend: 'SIDEBAR.NOTES',
            route: '/client-panel/notes',
            icon: 'AUDITS',
            subRoute: [
              {
                code: 'SCHEMES',
                legend: 'SIDEBAR.SCHEMES',
                route: '/client-panel/notes/schemes'
              },
              {
                code: 'SELF-CONTROL',
                legend: 'SIDEBAR.SELF-CONTROL',
                route: '/client-panel/notes/self-control'
              }
            ],
          },

          // {
          //   code: 'PURCHASE-RECORD',
          //   legend: 'SIDEBAR.PURCHASE-RECORD',
          //   route: '/client-panel/operations/purchase-record',
          //   icon: 'PURCHASE-RECORD'
          // },
          // {
          //   code: 'SALES-RECORD',
          //   legend: 'SIDEBAR.SALES-RECORD',
          //   route: '/client-panel/operations/sales-record',
          //   icon: 'SALES-RECORD'
          // },
          {
            code: 'SUPPORT',
            legend: 'SIDEBAR.SUPPORT',
            route: '/client-panel/support',
            icon: 'SUPPORT'
          },
          {
            code: 'SAFEGIS',
            legend: 'SAFEGIS',
            route: `${environment.gpaUrl}/#/login/${token}`,
            external: true,
            icon: 'GLOBE'
          }
        ];
        if (user.authorities.some((item: string) => item === 'ROLE_ACELEN_FARMING')) {
          this.clientPermissions = [
            {
              code: 'OPERATION-PLAN',
              legend: 'SIDEBAR.OPERATION-PLAN.TITLE',
              route: '/client-panel/operation-plan',
              icon: 'OPERATION-PLAN',
              subRoute: [
                {
                  code: 'SERVICE-ORDER',
                  legend: 'SIDEBAR.SERVICE-ORDER',
                  route: '/client-panel/operation-plan/service-order',
                  icon: 'SERVICE-ORDER'
                },
                {
                  code: 'FARMING',
                  legend: 'SIDEBAR.OPERATION.FARMING.TITLE',
                  route: '/client-panel/operation-plan/farming/',
                  subRoute: [
                    {
                      code: 'PRE-PLANTING-OPERATION',
                      legend: 'SIDEBAR.OPERATION.FARMING.PRE-PLANTING-OPERATION.TITLE',
                      route: '/client-panel/operation-plan/farming/pre-planting-operation',
                      subRoute: [
                        {
                          code: 'AREA-CLEANING',
                          legend: 'SIDEBAR.OPERATION.FARMING.PRE-PLANTING-OPERATION.AREA-CLEANING',
                          route: '/client-panel/operation-plan/farming/pre-planting-operation/area-cleaning/67ff8c143f77c6087f1be194',
                        },
                        {
                          code: 'PEST-MANAGEMENT',
                          legend: 'SIDEBAR.OPERATION.FARMING.PRE-PLANTING-OPERATION.PEST-MANAGEMENT',
                          route: '/client-panel/operation-plan/farming/pre-planting-operation/pest-management/67ff8d793f77c6087f1be1aa',
                        },
                        {
                          code: 'SOIL-CORRECTION',
                          legend: 'SIDEBAR.OPERATION.FARMING.PRE-PLANTING-OPERATION.SOIL-CORRECTION',
                          route: '/client-panel/operation-plan/farming/pre-planting-operation/soil-correction/67ff8ec83f77c6087f1be1c0',
                        },
                        {
                          code: 'DIGGING-OF-GRAVES',
                          legend: 'SIDEBAR.OPERATION.FARMING.PRE-PLANTING-OPERATION.DIGGING-OF-GRAVES',
                          route: '/client-panel/operation-plan/farming/pre-planting-operation/digging-of-graves/67ff90f43f77c6087f1be1d2',
                        },
                        {
                          code: 'IRRIGATION',
                          legend: 'SIDEBAR.OPERATION.FARMING.PRE-PLANTING-OPERATION.IRRIGATION',
                          route: '/client-panel/operation-plan/farming/pre-planting-operation/irrigation/67ff90443f77c6087f1be1d1',
                        },
                      ],
                    },
                    {
                      code: 'PLANTING-PHASE',
                      legend: 'SIDEBAR.OPERATION.FARMING.PLANTING-PHASE.TITLE',
                      route: '/client-panel/operation-plan/farming/planting-phase',
                      subRoute: [
                        {
                          code: 'FOUNDATION-FERTILIZATION',
                          legend: 'SIDEBAR.OPERATION.FARMING.PLANTING-PHASE.FOUNDATION-FERTILIZATION',
                          route: '/client-panel/operation-plan/farming/planting-phase/foundation-fertilization/67ff93363f77c6087f1be1e5',
                        },
                        {
                          code: 'PLANTING',
                          legend: 'SIDEBAR.OPERATION.FARMING.PLANTING-PHASE.PLANTING',
                          route: '/client-panel/operation-plan/farming/planting-phase/planting/67ff92a03f77c6087f1be1e4',
                        },
                      ],
                    },
                    {
                      code: 'POST-PLANT-PHASE',
                      legend: 'SIDEBAR.OPERATION.FARMING.POST-PLANT-PHASE.TITLE',
                      route: '/client-panel/operation-plan/farming/post-planting-phase',
                      subRoute: [
                        {
                          code: 'FERTILIZATION-WITH-IRRIGATION',
                          legend: 'SIDEBAR.OPERATION.FARMING.POST-PLANT-PHASE.FERTILIZATION-WITH-IRRIGATION',
                          route: '/client-panel/operation-plan/farming/post-planting-phase/fertilization-with-irrigation/67ff94fe3f77c6087f1be1e7',
                        },
                        {
                          code: 'PHYTOSANITARY-CONTROL',
                          legend: 'SIDEBAR.OPERATION.FARMING.POST-PLANT-PHASE.PHYTOSANITARY-CONTROL',
                          route: '/client-panel/operation-plan/farming/post-planting-phase/phytosanitary-control/67ff956b3f77c6087f1be1e8',
                        },
                        {
                          code: 'CHEMICAL-CROWNING',
                          legend: 'SIDEBAR.OPERATION.FARMING.POST-PLANT-PHASE.CHEMICAL-CROWNING',
                          route: '/client-panel/operation-plan/farming/post-planting-phase/chemical-crowning/67ff95cc3f77c6087f1be1e9',
                        },
                        {
                          code: '"MECHANIZED-PHOSPHATING',
                          legend: 'SIDEBAR.OPERATION.FARMING.POST-PLANT-PHASE.MECHANIZED-PHOSPHATING',
                          route: '/client-panel/operation-plan/farming/post-planting-phase/mechanized-phosphating/67ff96223f77c6087f1be1ea',
                        },
                      ]
                    },
                    {
                      code: 'POST-PLANTING-IRRIGATION',
                      legend: 'SIDEBAR.OPERATION.FARMING.POST-PLANTING-IRRIGATION.TITLE',
                      route: '/client-panel/operation-plan/farming/post-planting-phase/post-planting-irrigation',
                      subRoute: [
                        {
                          code: 'IRRIGATION',
                          legend: 'SIDEBAR.OPERATION.FARMING.POST-PLANTING-IRRIGATION.IRRIGATION',
                          route: '/client-panel/operation-plan/farming/post-planting-irrigation/irrigation/67ff947a3f77c6087f1be1e6',
                        }
                      ]
                    },
                    {
                      code: 'INBOUND-LOGISTIC-OPERATION',
                      legend: 'SIDEBAR.OPERATION.FARMING.INBOUND-LOGISTIC-OPERATION.TITLE',
                      route: '/client-panel/operation-plan/farming/imbound-logistic-operation',
                      subRoute: [
                        {
                          code: 'TRANSPORT-OF-SEEDLINGS',
                          legend: 'SIDEBAR.OPERATION.FARMING.INBOUND-LOGISTIC-OPERATION.TRANSPORT-OF-SEEDLINGS',
                          route: '/client-panel/operation-plan/farming/imbound-logistic-operation/transport-of-seedlings/67ff92453f77c6087f1be1e3',
                        }
                      ]
                    },
                    {
                      code: 'HARVEST',
                      legend: 'SIDEBAR.OPERATION.FARMING.HARVEST.TITLE',
                      route: '/client-panel/operation-plan/farming/harvest',
                      subRoute: [
                        {
                          code: 'FRESH-FRUIT-HARVESTING',
                          legend: 'SIDEBAR.OPERATION.FARMING.HARVEST.FRESH-FRUIT-HARVESTING',
                          route: '/client-panel/operation-plan/farming/harvest/fresh-fruit-harvesting/67ff96803f77c6087f1be1eb',
                        },
                        {
                          code: 'MECHANIZED-LOADING',
                          legend: 'SIDEBAR.OPERATION.FARMING.HARVEST.MECHANIZED-LOADING',
                          route: '/client-panel/operation-plan/farming/harvest/mechanized-loading/67ff974b3f77c6087f1be1fa',
                        },
                        {
                          code: 'TRANSPORT-OF-FRESH-FRUIT',
                          legend: 'SIDEBAR.OPERATION.FARMING.HARVEST.TRANSPORT-OF-FRESH-FRUIT',
                          route: '/client-panel/operation-plan/farming/harvest/transport-of-fresh-fruit/67ff97a93f77c6087f1be1fb',
                        }
                      ]
                    },
                  ]
                },
              ],
            },
            {
              code: 'OPERATION',
              legend: 'SIDEBAR.OPERATION.TITLE',
              route: '/client-panel/operation',
              icon: 'OPERATION-PLAN',
              subRoute: [
                {
                  code: 'FARMING',
                  legend: 'SIDEBAR.OPERATION.FARMING.TITLE',
                  route: '/client-panel/operation/farming/',
                  subRoute: [
                    {
                      code: 'PRE-PLANTING-OPERATION',
                      legend: 'SIDEBAR.OPERATION.FARMING.PRE-PLANTING-OPERATION.TITLE',
                      route: '/client-panel/operation/farming/pre-planting-operation',
                      subRoute: [
                        {
                          code: 'AREA-CLEANING',
                          legend: 'SIDEBAR.OPERATION.FARMING.PRE-PLANTING-OPERATION.AREA-CLEANING',
                          route: '/client-panel/operation/farming/pre-planting-operation/area-cleaning/67fd726b7f73f4020c54101a',
                        },
                        {
                          code: 'PEST-MANAGEMENT',
                          legend: 'SIDEBAR.OPERATION.FARMING.PRE-PLANTING-OPERATION.PEST-MANAGEMENT',
                          route: '/client-panel/operation/farming/pre-planting-operation/pest-management/67fd72b47f73f4020c54101b',
                        },
                        {
                          code: 'SOIL-CORRECTION',
                          legend: 'SIDEBAR.OPERATION.FARMING.PRE-PLANTING-OPERATION.SOIL-CORRECTION',
                          route: '/client-panel/operation/farming/pre-planting-operation/soil-correction/67fd75d07f73f4020c541025',
                        },
                        {
                          code: 'DIGGING-OF-GRAVES',
                          legend: 'SIDEBAR.OPERATION.FARMING.PRE-PLANTING-OPERATION.DIGGING-OF-GRAVES',
                          route: '/client-panel/operation/farming/pre-planting-operation/digging-of-graves/67fd732b7f73f4020c54101d',
                        },
                        {
                          code: 'IRRIGATION',
                          legend: 'SIDEBAR.OPERATION.FARMING.PRE-PLANTING-OPERATION.IRRIGATION',
                          route: '/client-panel/operation/farming/pre-planting-operation/irrigation/67fd72e57f73f4020c54101c',
                        },
                      ],
                    },
                    {
                      code: 'PLANTING-PHASE',
                      legend: 'SIDEBAR.OPERATION.FARMING.PLANTING-PHASE.TITLE',
                      route: '/client-panel/operation/farming/planting-phase',
                      subRoute: [
                        {
                          code: 'FOUNDATION-FERTILIZATION',
                          legend: 'SIDEBAR.OPERATION.FARMING.PLANTING-PHASE.FOUNDATION-FERTILIZATION',
                          route: '/client-panel/operation/farming/planting-phase/foundation-fertilization/67fd73e17f73f4020c541020',
                        },
                        {
                          code: 'PLANTING',
                          legend: 'SIDEBAR.OPERATION.FARMING.PLANTING-PHASE.PLANTING',
                          route: '/client-panel/operation/farming/planting-phase/planting/67fd73a77f73f4020c54101f',
                        },
                      ],
                    },
                    {
                      code: 'POST-PLANT-PHASE',
                      legend: 'SIDEBAR.OPERATION.FARMING.POST-PLANT-PHASE.TITLE',
                      route: '/client-panel/operation/farming/post-planting-phase',
                      subRoute: [
                        {
                          code: 'FERTILIZATION-WITH-IRRIGATION',
                          legend: 'SIDEBAR.OPERATION.FARMING.POST-PLANT-PHASE.FERTILIZATION-WITH-IRRIGATION',
                          route: '/client-panel/operation/farming/post-planting-phase/fertilization-with-irrigation/67fd761e7f73f4020c541026',
                        },
                        {
                          code: 'PHYTOSANITARY-CONTROL',
                          legend: 'SIDEBAR.OPERATION.FARMING.POST-PLANT-PHASE.PHYTOSANITARY-CONTROL',
                          route: '/client-panel/operation/farming/post-planting-phase/phytosanitary-control/67fd76577f73f4020c541027',
                        },
                        {
                          code: 'CHEMICAL-CROWNING',
                          legend: 'SIDEBAR.OPERATION.FARMING.POST-PLANT-PHASE.CHEMICAL-CROWNING',
                          route: '/client-panel/operation/farming/post-planting-phase/chemical-crowning/67fd768d7f73f4020c541028',
                        },
                        {
                          code: '"MECHANIZED-PHOSPHATING',
                          legend: 'SIDEBAR.OPERATION.FARMING.POST-PLANT-PHASE.MECHANIZED-PHOSPHATING',
                          route: '/client-panel/operation/farming/post-planting-phase/mechanized-phosphating/67fd76c97f73f4020c541029',
                        },
                      ]
                    },
                    {
                      code: 'POST-PLANTING-IRRIGATION',
                      legend: 'SIDEBAR.OPERATION.FARMING.POST-PLANTING-IRRIGATION.TITLE',
                      route: '/client-panel/operation/farming/post-planting-irrigation',
                      subRoute: [
                        {
                          code: 'IRRIGATION',
                          legend: 'SIDEBAR.OPERATION.FARMING.POST-PLANTING-IRRIGATION.IRRIGATION',
                          route: '/client-panel/operation/farming/post-planting-irrigation/irrigation/67fd74417f73f4020c541021',
                        }
                      ]
                    },
                    {
                      code: 'INBOUND-LOGISTIC-OPERATION',
                      legend: 'SIDEBAR.OPERATION.FARMING.INBOUND-LOGISTIC-OPERATION.TITLE',
                      route: '/client-panel/operation/farming/imbound-logistic-operation',
                      subRoute: [
                        {
                          code: 'TRANSPORT-OF-SEEDLINGS',
                          legend: 'SIDEBAR.OPERATION.FARMING.INBOUND-LOGISTIC-OPERATION.TRANSPORT-OF-SEEDLINGS',
                          route: '/client-panel/operation/farming/imbound-logistic-operation/transport-of-seedlings/67fd736b7f73f4020c54101e',
                        }
                      ]
                    },
                    {
                      code: 'HARVEST',
                      legend: 'SIDEBAR.OPERATION.FARMING.HARVEST.TITLE',
                      route: '/client-panel/operation/farming/harvest',
                      subRoute: [
                        {
                          code: 'FRESH-FRUIT-HARVESTING',
                          legend: 'SIDEBAR.OPERATION.FARMING.HARVEST.FRESH-FRUIT-HARVESTING',
                          route: '/client-panel/operation/farming/harvest/fresh-fruit-harvesting/67fd747e7f73f4020c541022',
                        },
                        {
                          code: 'MECHANIZED-LOADING',
                          legend: 'SIDEBAR.OPERATION.FARMING.HARVEST.MECHANIZED-LOADING',
                          route: '/client-panel/operation/farming/harvest/mechanized-loading/67fd74b47f73f4020c541023',
                        },
                        {
                          code: 'TRANSPORT-OF-FRESH-FRUIT',
                          legend: 'SIDEBAR.OPERATION.FARMING.HARVEST.TRANSPORT-OF-FRESH-FRUIT',
                          route: '/client-panel/operation/farming/harvest/transport-of-fresh-fruit/67fd74e67f73f4020c541024',
                        }
                      ]
                    },
                  ]
                },
              ],
            },
          ];
        }
      }

      if (this.systemType === 'ASSAI' || this.systemType === 'SAFETRACE') {
        if (user.authorities.some((item: string) => item === 'ROLE_SAFE_FLV_NAC')) {
          this.clientPermissions = [
            {
              code: 'INSTRUCTIONS',
              legend: 'SIDEBAR.INSTRUCTIONS',
              route: '/client-panel/instructions',
              icon: 'INSTRUCTIONS',
            },
            {
              code: 'DASHBOARD',
              legend: 'SIDEBAR.DASHBOARD',
              route: '/client-panel/dashboard',
              icon: 'DASHBOARD',
            },
            {
              code: 'PURCHASE-RECORD',
              legend: 'Registro de Compras',
              route: '/client-panel/operations/purchase-record',
              icon: 'PURCHASE-RECORD'
            },
            {
              code: 'SALES-RECORD',
              legend: 'SIDEBAR.SALES-RECORD',
              route: '/client-panel/operations/sales-record',
              icon: 'SALES-RECORD'
            },
            {
              code: 'MY-DATA',
              legend: 'SIDEBAR.MY-DATA',
              route: '/client-panel/register/my-data',
              icon: 'MY-DATA'
            },
            {
              code: 'CLIENTS',
              legend: 'SIDEBAR.CLIENTS',
              route: '/client-panel/clients',
              icon: 'CLIENTS'
            },
            {
              code: 'PRODUCTS',
              legend: 'SIDEBAR.PRODUCTS',
              route: '/client-panel/register/products',
              icon: 'PRODUCTS'
            },
            {
              code: 'PROVIDERS',
              legend: 'SIDEBAR.PROVIDERS',
              route: '/client-panel/register/providers',
              icon: 'PROVIDER',
            },
            {
              code: 'CHECKLISTS',
              legend: 'SIDEBAR.CHECKLISTS',
              route: '/client-panel/register/macroprocess/checklists',
              icon: 'CHECKLISTS',
            },
            {
              code: 'LABEL-CONFIG',
              legend: 'SIDEBAR.LABEL-CONFIGURATION',
              route: '/client-panel/label-config',
              icon: 'LABEL'
            }
          ];
        } else if (user.authorities.some((item: string) => item === 'ROLE_SAFETRACE_FEBRABAN_ADMIN')) {
          this.clientPermissions = [
            {
              code: 'INSTRUCTIONS',
              legend: 'SIDEBAR.INSTRUCTIONS',
              route: '/client-panel/instructions',
              icon: 'INSTRUCTIONS',
            },
            {
              code: 'DASHBOARD',
              legend: 'SIDEBAR.DASHBOARD',
              route: '/client-panel/dashboard',
              icon: 'DASHBOARD',
            }, {
              code: 'PROVIDERS',
              legend: 'SIDEBAR.PROVIDERS',
              route: '/client-panel/register/providers',
              icon: 'PROVIDER',
            },
            {
              code: 'PURCHASE-RECORD',
              legend: 'Registro de Compras',
              route: '/client-panel/purchase-record',
              icon: 'PURCHASE-RECORD'
            },
            {
              code: 'PRODUCTS',
              legend: 'SIDEBAR.PRODUCTS',
              route: '/client-panel/register/products',
              icon: 'PRODUCTS'
            },
            {
              code: 'SUPPORT',
              legend: 'SIDEBAR.SUPPORT',
              route: '/client-panel/support',
              icon: 'SUPPORT'
            },
            {
              code: 'SAFEGIS',
              legend: 'SAFEGIS',
              route: `${environment.gpaUrl}/#/login/${this.token}`,
              external: true,
              icon: 'GLOBE'
            },
          ];
        }
      }
      if (this.systemType === 'GRAOSDOPARA') {
        this.clientPermissions = [
          {
            code: 'HOMOLOGATION-PROCESS',
            legend: 'SIDEBAR.HOMOLOGATION-PROCESS',
            route: '/client-panel/register/macroprocess/homologation-process',
            icon: 'CERTIFICATION-REQUEST',
            subRoute: [],
          }
        ];
      }

      if (this.systemType === 'INTERSTATE_MEAT_DISTRIBUTORS') {
        this.clientPermissions.forEach((item, index) => {
          if (item.code === 'CT') {
            this.clientPermissions.splice(index, 1);
          }
        });
      }

      if (this.systemType === 'ASSAI') {
        this.clientPermissions = this.getBaseProviderPermissions(true);
      }
    }

    this.customerIdSubscription = this.clientProviderService.customerId$.subscribe((customerId => {
      if (customerId) {
        this.enterpriseService.setCustomerId(customerId);
        if (this.systemType === 'ACELEN') {
          let permissions = this.clientPermissions;
          if (this.menus()) {
            permissions = this.menus() as iSideBarMenuItem[];
          }
          let item: any = permissions.find(item => item.code === 'REGISTER');
          item = item.subRoute.find((item: any) => item.code === 'MACROPROCESS');
          item = item.subRoute.find((item: any) => item.code === 'HOMOLOGATION-PROCESS');

          if (this.schemesList().length) {
            this.schemesList().forEach((groupItem: any) => {
              groupItem.groups.forEach((element: any) => {
                if (item?.subRoute) {
                  item.subRoute.push({
                    code: element.code,
                    legend: element.name,
                    route: '/client-panel/register/macroprocess/homologation-process/' + element.id,
                  });
                }
              });
              this.setMenu(permissions as iSideBarMenuItem[]);
            });
          } else {
            const userItem = sessionStorage.getItem('user');
            const user = userItem ? JSON.parse(userItem) : null;
            const userId = user.userId;
            this.formGroupService.findByCustomerId(this.customerId() as string, userId).subscribe({
              next: (res: any) => {
                this.enterpriseService.setSchemesList(res);
                if (res?.length) {
                  res.forEach((groupItem: any) => {
                    groupItem.groups.forEach((element: any) => {
                      if (item?.subRoute) {
                        item.subRoute.push({
                          code: element.code,
                          legend: element.name,
                          route: '/client-panel/register/macroprocess/homologation-process/' + element.id,
                        });
                      }
                    });
                  });
                  this.setMenu(permissions as iSideBarMenuItem[]);
                }
              },
            });
          }

        } else {
          let permissions = this.clientPermissions;
          if (this.menus()) {
            permissions = this.menus() as iSideBarMenuItem[];
          }
          const homologationProcess: iSideBarMenuItem | undefined = permissions.find(item => item.code === 'HOMOLOGATION-PROCESS');
          const ct: iSideBarMenuItem | undefined = permissions.find(item => item.code === 'CT');
          const registerSchemas: iSideBarMenuItem | undefined = permissions.find(item => item.code === 'CADASTRO');
          const operations: iSideBarMenuItem | undefined = permissions.find(item => item.code === 'operations');
          const documents: iSideBarMenuItem | undefined = permissions.find(item => item.code === 'documents');
          const evaluations: iSideBarMenuItem | undefined = permissions.find(item => item.code === 'EVALUATIONS');

          if (homologationProcess) {
            // homologationProcess.subRoute = [];
          }

          if (ct) {
            // ct.subRoute = [];
          }

          if (registerSchemas) {
            // registerSchemas.subRoute = [];
          }

          if (operations) {
            // operations.subRoute = [];
          }

          if (documents) {
            // documents.subRoute = [];
          }
          if (evaluations) {
            // evaluations.subRoute = [];
          }

          function setDynamicSchemes(schemes: any[]) {
            schemes.forEach((groupItem: any) => {
              groupItem.groups.forEach((element: any) => {
                const menuItem: iSideBarMenuItem | undefined = {
                  code: element.code,
                  legend: element.name,
                  route: '/client-panel/ct/' + element.id,
                };
                switch (element?.typeSchema) {
                  case 'CT':
                    if (ct?.subRoute && !(ct.subRoute.some(item => item.route === ('/client-panel/ct/' + element.id)))) {
                      menuItem.route = '/client-panel/ct/' + element.id;
                      ct.subRoute.push(menuItem);
                    }
                    break;
                  case 'CADASTRO':
                    if (registerSchemas?.subRoute && !(registerSchemas.subRoute.some(item => item.route === ('/client-panel/register-schemas/' + element.id)))) {
                      menuItem.route = '/client-panel/register-schemas/' + element.id;
                      registerSchemas.subRoute.push(menuItem);
                    }
                    break;
                  case 'OPERACOES':
                    if (operations?.subRoute && !(operations.subRoute.some(item => item.route === ('/client-panel/operations/' + element.id)))) {
                      menuItem.route = '/client-panel/scheme-operations/' + element.id;
                      operations.subRoute.push(menuItem);
                    }
                    break;
                  case 'DOCUMENTOS':
                    if (documents?.subRoute && !(documents.subRoute.some(item => item.route === ('/client-panel/documents/' + element.id)))) {
                      menuItem.route = '/client-panel/documents/' + element.id;
                      documents.subRoute.push(menuItem);
                    }
                    break;
                  case 'AVALIACOES':
                    if (evaluations?.subRoute && !(evaluations.subRoute.some(item => item.route === ('/client-panel/evaluations/' + element.id)))) {
                      menuItem.route = '/client-panel/evaluations/' + element.id;
                      evaluations.subRoute.push(menuItem);
                    }
                    break;
                  default:
                    if (homologationProcess?.subRoute && !(homologationProcess.subRoute.some(item => item.route === ('/client-panel/register/macroprocess/homologation-process/' + element.id)))) {
                      menuItem.route = '/client-panel/register/macroprocess/homologation-process/' + element.id;
                      homologationProcess.subRoute.push(menuItem);
                    }
                    break;
                }
              });
            });
          }

          function filterSchemes(schemes: any) {
            if (schemes.length) {
              schemes = schemes[0].groups;
            }
            if (ct) {
              ct.subRoute = ct.subRoute?.filter((item: any) =>  schemes.some((scheme: any) => scheme.code === item.code));
            }
            if (operations) {
              operations.subRoute = operations.subRoute?.filter((item: any) =>  schemes.some((scheme: any) => scheme.code === item.code));
            }
            if (documents) {
              documents.subRoute = documents.subRoute?.filter((item: any) =>  schemes.some((scheme: any) => scheme.code === item.code));
            }
            if (evaluations) {
              evaluations.subRoute = evaluations.subRoute?.filter((item: any) =>  schemes.some((scheme: any) => scheme.code === item.code));
            }
          }

          const userItem = sessionStorage.getItem('user');
          const user = userItem ? JSON.parse(userItem) : null;
          const userId = user.userId;
          
          this.formGroupService.findByCustomerId(this.customerId() as string, userId).subscribe({
            next: (res: any) => {
              if (res?.length) {
                this.enterpriseService.setSchemesList(res);
                setDynamicSchemes(res);
              }
              filterSchemes(res);
              this.setMenu(permissions as iSideBarMenuItem[]);
            },
          });

        }
      } else {
        let permissions = this.clientPermissions;
        if (this.menus()) {
          permissions = this.menus() as iSideBarMenuItem[];
        }
        const homologationProcess: iSideBarMenuItem | undefined = permissions.find(item => item.code === 'HOMOLOGATION-PROCESS');
        const ct: iSideBarMenuItem | undefined = permissions.find(item => item.code === 'CT');

        if (homologationProcess) {
          homologationProcess.subRoute = [];
        }

        if (ct) {
          ct.subRoute = [];
        }
        if (this.schemesList().length) {
          this.schemesList().forEach((groupItem: any) => {
            groupItem.groups.forEach((element: any) => {
              if (element?.typeSchema === 'CT' && ct) {
                if (ct?.subRoute && !(ct.subRoute.some(item => item.route == ('/client-panel/ct/' + element.id)))) {
                  ct.subRoute.push({
                    code: element.code,
                    legend: element.name,
                    route: '/client-panel/ct/' + element.id,
                  });
                }
              } else if (homologationProcess?.subRoute && !(homologationProcess.subRoute.some(item => item.route == ('/client-panel/register/macroprocess/homologation-process/' + element.id)))) {
                homologationProcess.subRoute.push({
                  code: element.code,
                  legend: element.name,
                  route: '/client-panel/register/macroprocess/homologation-process/' + element.id,
                });
              }
            });
            this.setMenu(permissions as iSideBarMenuItem[]);
          });
        }
      }
    }));
  }

  private getBaseProviderPermissions(includeReports?: boolean): iSideBarMenuItem[] {
    const base: iSideBarMenuItem[] = this.clientPermissions = [
      {
        code: 'INSTRUCTIONS',
        legend: 'SIDEBAR.INSTRUCTIONS',
        route: '/client-panel/instructions',
        icon: 'INSTRUCTIONS',
      },
      {
        code: 'DASHBOARD',
        legend: 'SIDEBAR.DASHBOARD',
        route: '/client-panel/dashboard',
        icon: 'DASHBOARD',
      },
      {
        code: 'HOMOLOGATION-PROCESS',
        legend: 'SIDEBAR.HOMOLOGATION-PROCESS',
        route: '/client-panel/register/macroprocess/homologation-process',
        icon: 'MAIL',
        subRoute: [],
      },
      {
        code: 'CT',
        legend: 'CT',
        route: '/client-panel/ct',
        icon: 'MAIL',
        subRoute: [],
      },
      {
        code: 'NOTES',
        legend: 'SIDEBAR.NOTES',
        route: '/client-panel/notes',
        icon: 'AUDITS',
        subRoute: [
          {
            code: 'SCHEMES',
            legend: 'SIDEBAR.SCHEMES',
            route: '/client-panel/notes/schemes'
          },
          {
            code: 'SELF-CONTROL',
            legend: 'SIDEBAR.SELF-CONTROL',
            route: '/client-panel/notes/self-control'
          }
        ],
      },
      ...(includeReports ? [{
        code: 'TRACEABILITY',
        legend: 'Rastreabilidade',
        route: '/client-panel/traceability',
        icon: 'TRACEABILITY',
        subRoute: [
          {
            code: 'REPORTS',
            legend: 'Relatórios',
            route: '/client-panel/traceability/reports'
          }
        ],
      }] : []),
      {
        code: 'MY-DATA',
        legend: 'SIDEBAR.MY-DATA',
        route: '/client-panel/register/my-data',
        icon: 'MY-DATA'
      },
      {
        code: 'CLIENTS',
        legend: 'SIDEBAR.CLIENTS',
        route: '/client-panel/clients',
        icon: 'CLIENTS'
      },
      {
        code: 'PROVIDERS',
        legend: 'SIDEBAR.PROVIDERS',
        route: '/client-panel/register/providers',
        icon: 'PROVIDER',
      },
      {
        code: 'PRODUCTS',
        legend: 'SIDEBAR.PRODUCTS',
        route: '/client-panel/register/products',
        icon: 'PRODUCTS'
      },
      {
        code: 'ASSOCIATE',
        legend: 'SIDEBAR.ASSOCIATE',
        route: '/client-panel/register/macroprocess/associate',
        icon: 'ASSOCIATE'
      },
      {
        code: 'CHECKLISTS',
        legend: 'SIDEBAR.CHECKLISTS',
        route: '/client-panel/register/macroprocess/checklists',
        icon: 'CHECKLISTS',
      },
      // {
      //   code: 'AUDITS',
      //   legend: 'Auditorias',
      //   route: '/client-panel/audits',
      //   icon: 'AUDITS'
      // },
      // {
      //   code: 'INPUT-LIST',
      //   legend: 'Lista de Insumos',
      //   route: '/client-panel/input-list',
      //   icon: 'CHECKLISTS'
      // },
      // {
      //   code: 'PRODUCTION-ORDER',
      //   legend: 'SIDEBAR.PRODUCTION-ORDER',
      //   route: '/client-panel/production-order',
      //   icon: 'PRODUCTS'
      // },
      {
        code: 'PURCHASE-RECORD',
        legend: 'SIDEBAR.PURCHASE-RECORD',
        route: this.systemType === 'ASSAI' ? '/client-panel/purchase' : '/client-panel/operations/purchase-record',
        icon: 'PURCHASE-RECORD'
      },
      {
        code: 'SALES-RECORD',
        legend: 'SIDEBAR.SALES-RECORD',
        route: '/client-panel/operations/sales-record',
        icon: 'SALES-RECORD'
      },
      // {
      //   code: 'DATASHEET',
      //   legend: 'Lista Técnica',
      //   route: '/client-panel/datasheet',
      //   icon: 'TECHNICAL_SHEET',
      // },
      // {
      //   code: 'CERTIFICATES',
      //   legend: 'SIDEBAR.CERTIFICATES',
      //   route: '/client-panel/certificates',
      //   icon: 'CERTIFICATES'
      // },
      {
        code: 'LABEL-CONFIG',
        legend: 'SIDEBAR.LABEL-CONFIGURATION',
        route: '/client-panel/label-config',
        icon: 'LABEL'
      },
      {
        code: 'SUPPORT',
        legend: 'SIDEBAR.SUPPORT',
        route: '/client-panel/support',
        icon: 'SUPPORT'
      },
      {
        code: 'SAFEGIS',
        legend: 'SAFEGIS',
        route: `${environment.gpaUrl}/#/login/${this.token}`,
        external: true,
        icon: 'GLOBE'
      },
      {
        code: 'MASTERBOI',
        legend: 'Qualificação de Fornecedores',
        route: `${environment.qualificacaoFornecedoresUrl}/#/safetrace/login/${this.token}`,
        external: true,
        icon: 'GLOBE'
      }
    ];
    return base;
  }

  private getBaseAdminPermissions(includeCTList: boolean, includeReports?: boolean): iSideBarMenuItem[] {
    const base: iSideBarMenuItem[] = this.adminPermissions = [
      {
        code: 'DASHBOARD',
        legend: 'SIDEBAR.DASHBOARD',
        route: '/panel/dashboard',
        icon: 'DASHBOARD',
      },
      {
        code: 'CLIENTS',
        legend: 'SIDEBAR.CLIENTS',
        route: '/panel/register/clients',
        icon: 'CLIENTS',
      },
      {
        code: 'CERTIFICATIONS',
        legend: 'SIDEBAR.CERTIFICATIONS',
        route: '/panel/schemes/certifications',
        subRoute: [
          {
            code: 'INSPECTION-PREPARATION',
            legend: 'Pré Avaliação',
            route: '/panel/schemes/certifications/inspection-preparation',
            subRoute: [
              {
                code: 'PENDING-INFO',
                legend: 'SIDEBAR.PENDING-INFO',
                route:
                  '/panel/schemes/certifications/inspection-preparation/pending-info',
              },
              {
                code: 'PENDING-APROVE',
                legend: 'SIDEBAR.PENDING-APROVE',
                route:
                  '/panel/schemes/certifications/inspection-preparation/pending-approval',
              },
            ],
          },
          {
            code: 'INSPECTION',
            legend: 'SIDEBAR.INSPECTION',
            route: '/panel/schemes/certifications/inspection',
            subRoute: [
              {
                code: 'PENDING-INFO',
                legend: 'SIDEBAR.PENDING-INFO',
                route:
                  '/panel/schemes/certifications/inspection/pending-info',
              },
              {
                code: 'PENDING-APROVE',
                legend: 'SIDEBAR.PENDING-APROVE',
                route:
                  '/panel/schemes/certifications/inspection/pending-approval',
              },
            ],
          },
          {
            code: 'INSPECTION-DECISION',
            legend: 'SIDEBAR.INSPECTION-DECISION',
            route: '/panel/schemes/certifications/inspection-decision',
            subRoute: [
              {
                code: 'APPROVED',
                legend: 'SIDEBAR.APPROVED',
                route:
                  '/panel/schemes/certifications/inspection-decision/approved',
              },
              {
                code: 'FAILED',
                legend: 'SIDEBAR.FAILED',
                route:
                  '/panel/schemes/certifications/inspection-decision/failed',
              },
            ],
          },
        ],
        icon: 'CERTIFICATIONS',
      },
      ...(includeCTList ? [{
        code: 'CT-LIST',
        legend: 'CT',
        route: '/panel/schemes/ct',
        icon: 'PRODUCTS',
        subRoute: [
          {
            code: 'INSPECTION-PREPARATION',
            legend: 'Pré Avaliação',
            route: '/panel/schemes/ct/inspection-preparation',
            subRoute: [
              {
                code: 'PENDING-INFO',
                legend: 'SIDEBAR.PENDING-INFO',
                route:
                  '/panel/schemes/ct/inspection-preparation/pending-info',
              },
              {
                code: 'PENDING-APROVE',
                legend: 'SIDEBAR.PENDING-APROVE',
                route:
                  '/panel/schemes/ct/inspection-preparation/pending-approval',
              },
            ],
          },
          {
            code: 'INSPECTION',
            legend: 'SIDEBAR.INSPECTION',
            route: '/panel/schemes/ct/inspection',
            subRoute: [
              {
                code: 'PENDING-INFO',
                legend: 'SIDEBAR.PENDING-INFO',
                route: '/panel/schemes/ct/inspection/pending-info',
              },
              {
                code: 'PENDING-APROVE',
                legend: 'SIDEBAR.PENDING-APROVE',
                route: '/panel/schemes/ct/inspection/pending-approval',
              },
            ],
          },
          {
            code: 'INSPECTION-DECISION',
            legend: 'SIDEBAR.INSPECTION-DECISION',
            route: '/panel/schemes/ct/inspection-decision',
            subRoute: [
              {
                code: 'APPROVED',
                legend: 'SIDEBAR.APPROVED',
                route: '/panel/schemes/ct/inspection-decision/approved',
              },
              {
                code: 'FAILED',
                legend: 'SIDEBAR.FAILED',
                route: '/panel/schemes/ct/inspection-decision/failed',
              },
            ],
          },
        ],
      }] : []),
      ...(includeReports ? [{
        code: 'TRACEABILITY',
        legend: 'Rastreabilidade',
        route: '/panel/traceability',
        icon: 'TRACEABILITY',
        subRoute: [
          {
            code: 'REPORT',
            legend: 'Relatórios',
            route: '/panel/traceability/reports'
          }
        ],
      }] : []),
      {
        code: 'SELF-CONTROL',
        legend: 'Autocontrole',
        route: '/panel/schemes/self-control',
        icon: 'AUDITS',
        subRoute: [
          {
            code: 'INSPECTION-PREPARATION',
            legend: 'Pré Avaliação',
            route: '/panel/schemes/self-control/inspection-preparation',
            subRoute: [
              {
                code: 'PENDING-INFO',
                legend: 'SIDEBAR.PENDING-INFO',
                route:
                  '/panel/schemes/self-control/inspection-preparation/pending-info',
              },
              {
                code: 'PENDING-APROVE',
                legend: 'SIDEBAR.PENDING-APROVE',
                route:
                  '/panel/schemes/self-control/inspection-preparation/pending-approval',
              },
            ],
          },
          {
            code: 'INSPECTION',
            legend: 'SIDEBAR.INSPECTION',
            route: '/panel/schemes/self-control/inspection',
            subRoute: [
              {
                code: 'PENDING-INFO',
                legend: 'SIDEBAR.PENDING-INFO',
                route: '/panel/schemes/self-control/inspection/pending-info',
              },
              {
                code: 'PENDING-APROVE',
                legend: 'SIDEBAR.PENDING-APROVE',
                route:
                  '/panel/schemes/self-control/inspection/pending-approval',
              },
            ],
          },
          {
            code: 'INSPECTION-DECISION',
            legend: 'SIDEBAR.INSPECTION-DECISION',
            route: '/panel/schemes/self-control/inspection-decision',
            subRoute: [
              {
                code: 'APPROVED',
                legend: 'SIDEBAR.APPROVED',
                route:
                  '/panel/schemes/self-control/inspection-decision/approved',
              },
              {
                code: 'FAILED',
                legend: 'SIDEBAR.FAILED',
                route:
                  '/panel/schemes/self-control/inspection-decision/failed',
              },
            ],
          },
        ],
      },
      {
        code: 'NOTES',
        legend: 'Apontamentos',
        route: '/panel/schemes/notes',
        icon: 'MINERVA',
        subRoute: [
          {
            code: 'INSPECTION-PREPARATION',
            legend: 'Pré Avaliação',
            route: '/panel/schemes/notes/inspection-preparation',
            subRoute: [
              {
                code: 'PENDING-INFO',
                legend: 'SIDEBAR.PENDING-INFO',
                route:
                  '/panel/schemes/notes/inspection-preparation/pending-info',
              },
              {
                code: 'PENDING-APROVE',
                legend: 'SIDEBAR.PENDING-APROVE',
                route:
                  '/panel/schemes/notes/inspection-preparation/pending-approval',
              },
            ],
          },
          {
            code: 'INSPECTION',
            legend: 'SIDEBAR.INSPECTION',
            route: '/panel/schemes/notes/inspection',
            subRoute: [
              {
                code: 'PENDING-INFO',
                legend: 'SIDEBAR.PENDING-INFO',
                route: '/panel/schemes/notes/inspection/pending-info',
              },
              {
                code: 'PENDING-APROVE',
                legend: 'SIDEBAR.PENDING-APROVE',
                route: '/panel/schemes/notes/inspection/pending-approval',
              },
            ],
          },
          {
            code: 'INSPECTION-DECISION',
            legend: 'SIDEBAR.INSPECTION-DECISION',
            route: '/panel/schemes/notes/inspection-decision',
            subRoute: [
              {
                code: 'APPROVED',
                legend: 'SIDEBAR.APPROVED',
                route: '/panel/schemes/notes/inspection-decision/approved',
              },
              {
                code: 'FAILED',
                legend: 'SIDEBAR.FAILED',
                route: '/panel/schemes/notes/inspection-decision/failed',
              },
            ],
          },
        ],
      },
      {
        code: 'ASSOCIATE',
        legend: 'SIDEBAR.ASSOCIATE',
        route: '/panel/settings/associate',
        icon: 'ASSOCIATE',
      },
      {
        code: 'CHECKLISTS',
        legend: 'SIDEBAR.CHECKLISTS',
        route: '/panel/checklists',
        icon: 'CHECKLISTS',
      },
      {
        code: 'SALES-RECORD',
        legend: 'SIDEBAR.SALES-RECORD',
        route: '/panel/operations/sales-record',
        icon: 'SALES-RECORD',
      },
      {
        code: 'COMUNICATION',
        legend: 'SIDEBAR.COMMUNICATION',
        route: '/panel/comunication',
        icon: 'COMUNICATION',
      },
      {
        code: 'SUPPORT',
        legend: 'SIDEBAR.SUPPORT',
        route: '/panel/support',
        icon: 'SUPPORT',
      },
      {
        code: 'SAFEGIS',
        legend: 'SAFEGIS',
        route: `https://renovar.agrotransparencia.com.br/login/${this.token}`,
        external: true,
        icon: 'GLOBE',
      },
    ];
    return base;
  }

  getSchemesListAdmin() {
    let permissions = this.adminPermissions;
    if (this.menus()) {
      permissions = this.menus() as iSideBarMenuItem[];
    }
    const certifications: iSideBarMenuItem | undefined = permissions.find(item => item.code === 'CERTIFICATIONS');
    const ct: iSideBarMenuItem | undefined = permissions.find(item => item.code === 'CT-LIST');
    const selfControl: iSideBarMenuItem | undefined = permissions.find(item => item.code === 'SELF-CONTROL');
    const notes: iSideBarMenuItem | undefined = permissions.find(item => item.code === 'NOTES');
    const register: iSideBarMenuItem | undefined = permissions.find(item => item.code === 'SCHEME-REGISTER');
    const operations: iSideBarMenuItem | undefined = permissions.find(item => item.code === 'OPERATIONS');
    const documents: iSideBarMenuItem | undefined = permissions.find(item => item.code === 'DOCUMENTS');
    const evaluations: iSideBarMenuItem | undefined = permissions.find(item => item.code === 'EVALUATIONS');

    if (certifications) {
      // certifications.subRoute = [];
    }
    if (ct) {
      // ct.subRoute = [];
    }
    if (selfControl) {
      // selfControl.subRoute = [];
    }

    if (notes) {
      // notes.subRoute = [];
    }

    if (register) {
      // register.subRoute = [];
    }

    if (operations) {
      // operations.subRoute = [];
    }

    if (documents) {
      // documents.subRoute = [];
    }

    if (evaluations) {
      // evaluations.subRoute = [];
    }

    const userItem = sessionStorage.getItem('user');
    const user = userItem ? JSON.parse(userItem) : null;
    if (!this.customerId()) {
      this.enterpriseService.setCustomerId(user.customerId);
    }

    function setDynamicSchemes(schemes: any[]) {
      schemes.forEach((element: any) => {
        element.groups.forEach((groupItem: IScheme) => {
          let schemeType = '';
          switch (groupItem?.typeSchema) {
            case 'APONTAMENTO':
              schemeType = 'note';
              break;
            case 'AUTOCONTROLE':
              schemeType = 'self-control';
              break;
            case 'CERTIFICACAO':
              schemeType = 'certifications';
              break;
            case 'CT':
              schemeType = 'ct';
              break;
            case 'CADASTRO':
              schemeType = 'register';
              break;
            case 'OPERACOES':
              schemeType = 'operations';
              break;
            case 'DOCUMENTOS':
              schemeType = 'documents';
              break;
            case 'AVALIACOES':
              schemeType = 'evaluations';
              break;
            default:
              schemeType = 'certifications';
          }

          let menuItem = {
            code: groupItem.code,
            legend: groupItem.name,
            route: `/panel/schemes/${schemeType}/${groupItem.id}`,
            subRoute: [
              // {
              //   code: 'INSPECTION-PREPARATION',
              //   legend: 'Pré Avaliação',
              //   route: `/panel/schemes/${schemeType}/${groupItem.id}/inspection-preparation`,
              //   subRoute: [
              //     {
              //       code: 'PENDING-INFO',
              //       legend: 'SIDEBAR.PENDING-INFO',
              //       route: `/panel/schemes/${schemeType}/${groupItem.id}/inspection-preparation/pending-info`,
              //     },
              //     {
              //       code: 'PENDING-APROVE',
              //       legend: 'SIDEBAR.PENDING-APROVE',
              //       route: `/panel/schemes/${schemeType}/${groupItem.id}/inspection-preparation/pending-approval`,
              //     },
              //   ],
              // },
              {
                code: 'INSPECTION',
                legend: 'SIDEBAR.INSPECTION',
                route: `/panel/schemes/${schemeType}/${groupItem.id}/inspection`,
                subRoute: [
                  {
                    code: 'PENDING-INFO',
                    legend: 'SIDEBAR.PENDING-INFO',
                    route: `/panel/schemes/${schemeType}/${groupItem.id}/inspection/pending-info`,
                  },
                  {
                    code: 'PENDING-APROVE',
                    legend: 'SIDEBAR.PENDING-APROVE',
                    route: `/panel/schemes/${schemeType}/${groupItem.id}/inspection/pending-approval`,
                  },
                ],
              },
              {
                code: 'INSPECTION-DECISION',
                legend: 'SIDEBAR.INSPECTION-DECISION',
                route: `/panel/schemes/${schemeType}/${groupItem.id}/inspection-decision`,
                subRoute: [
                  {
                    code: 'APPROVED',
                    legend: 'SIDEBAR.APPROVED',
                    route: `/panel/schemes/${schemeType}/${groupItem.id}/inspection-decision/approved`,
                  },
                  {
                    code: 'FAILED',
                    legend: 'SIDEBAR.FAILED',
                    route: `/panel/schemes/${schemeType}/${groupItem.id}/inspection-decision/failed`,
                  },
                ],
              },
            ],
          };

          if (groupItem.typeSchema === 'CT') {
            menuItem = {
              code: groupItem.code,
              legend: groupItem.name,
              route: `/panel/schemes/${schemeType}/${groupItem.id}`,
              subRoute: [
                {
                  code: 'IN-EVALUATION',
                  legend: 'SIDEBAR.IN-EVALUATION',
                  route: `/panel/schemes/${schemeType}/${groupItem.id}/in-evaluation`,
                  subRoute: [
                    {
                      code: 'PENDING-INFO',
                      legend: 'SIDEBAR.PENDING-INFO',
                      route: `/panel/schemes/${schemeType}/${groupItem.id}/in-evaluation/pending-info`,
                    },
                    {
                      code: 'PENDING-APROVE',
                      legend: 'SIDEBAR.PENDING-APROVE',
                      route: `/panel/schemes/${schemeType}/${groupItem.id}/in-evaluation/pending-approval`,
                    },
                  ]
                },
                {
                  code: 'AVALIATED',
                  legend: 'SIDEBAR.AVALIATED',
                  route: `/panel/schemes/${schemeType}/${groupItem.id}/avaliated`,
                  subRoute: [
                    {
                      code: 'APPROVED',
                      legend: 'SIDEBAR.APPROVED',
                      route: `/panel/schemes/${schemeType}/${groupItem.id}/avaliated/approved`,
                    },
                    {
                      code: 'FAILED',
                      legend: 'SIDEBAR.FAILED',
                      route: `/panel/schemes/${schemeType}/${groupItem.id}/avaliated/failed`,
                    },
                    {
                      code: 'FAILED',
                      legend: 'Finalizados',
                      route: `/panel/schemes/${schemeType}/${groupItem.id}/avaliated/finished`,
                    },
                    {
                      code: 'SUBSTITUTE',
                      legend: 'SIDEBAR.SUBSTITUTED',
                      route: `/panel/schemes/${schemeType}/${groupItem.id}/avaliated/substituted`,
                    },
                  ]
                }
              ]
            };
          }

          switch (groupItem?.typeSchema) {
            case 'APONTAMENTO':
              if (!notes?.subRoute?.some(item => item.route === menuItem.route)) {
                notes?.subRoute?.push(menuItem);
              }
              break;
            case 'AUTOCONTROLE':
              if (!selfControl?.subRoute?.some(item => item.route === menuItem.route)) {
                selfControl?.subRoute?.push(menuItem);
              }
              break;
            case 'CERTIFICACAO':
              if (!certifications?.subRoute?.some(item => item.route === menuItem.route)) {
                certifications?.subRoute?.push(menuItem);
              }
              break;
            case 'CT':
              if (!ct?.subRoute?.some(item => item.route === menuItem.route)) {
                ct?.subRoute?.push(menuItem);
              }
              break;
            case 'CADASTRO':
              if (!register?.subRoute?.some(item => item.route === menuItem.route)) {
                register?.subRoute?.push(menuItem);
              }
              break;
            case 'OPERACOES':
              if (!operations?.subRoute?.some(item => item.route === menuItem.route)) {
                operations?.subRoute?.push(menuItem);
              }
              break;
            case 'DOCUMENTOS':
              if (!documents?.subRoute?.some(item => item.route === menuItem.route)) {
                documents?.subRoute?.push(menuItem);
              }
              break;
            case 'AVALIACOES':
              if (!evaluations?.subRoute?.some(item => item.route === menuItem.route)) {
                evaluations?.subRoute?.push(menuItem);
              }
              break;
            default:
            // certifications?.subRoute?.push(menuItem);
          }
        });
      });
    }

    function filterSchemes(schemes: any) {
      if (schemes.length) {
        schemes = schemes[0].groups;
      }
      if (ct) {
        ct.subRoute = ct.subRoute?.filter((item: any) =>  schemes.some((scheme: any) => scheme.code === item.code));
      }
      if (selfControl) {
        selfControl.subRoute = selfControl.subRoute?.filter((item: any) =>  schemes.some((scheme: any) => scheme.code === item.code));
      }
      if (certifications) {
        certifications.subRoute = certifications.subRoute?.filter((item: any) =>  schemes.some((scheme: any) => scheme.code === item.code));
      }
      if (register) {
        register.subRoute = register.subRoute?.filter((item: any) =>  schemes.some((scheme: any) => scheme.code === item.code));
      }
      if (operations) {
        operations.subRoute = operations.subRoute?.filter((item: any) =>  schemes.some((scheme: any) => scheme.code === item.code));
      }
      if (documents) {
        documents.subRoute = documents.subRoute?.filter((item: any) =>  schemes.some((scheme: any) => scheme.code === item.code));
      }
      if (evaluations) {
        evaluations.subRoute = evaluations.subRoute?.filter((item: any) =>  schemes.some((scheme: any) => scheme.code === item.code));
      }
    }

    const userId = user.userId;
    if (!this.schemesList().length) {
      this.formGroupService.findByCustomerId(this.customerId() as string, userId).subscribe({
        next: (res: any) => {
          if (res?.length) {
            this.enterpriseService.setSchemesList(res);
            setDynamicSchemes(res);
          }
          filterSchemes(res);

          const userItem = sessionStorage.getItem('user');
          const user = userItem ? JSON.parse(userItem) : null;
          if (user?.authorities.some((item: string) => item === 'ROLE_SUPERADMIN')) {
            const menuItem = permissions.find((item: iSideBarMenuItem) => item.code === 'SETTINGS');
            if (menuItem) {
              menuItem.subRoute?.push({
                code: 'SYSTEM-CONFIGURATION',
                legend: 'Configuração de Temas do Sistema',
                route: '/panel/settings/system-config',
              });
            }
          }
          this.setMenu(permissions);
        },
      });
    } else {

      const userItem = sessionStorage.getItem('user');
      const user = userItem ? JSON.parse(userItem) : null;
      if (user?.authorities.some((item: string) => item === 'ROLE_SUPERADMIN')) {
        const menuItem = permissions.find((item: iSideBarMenuItem) => item.code === 'SETTINGS');
        if (menuItem) {
          menuItem.subRoute?.push({
            code: 'SYSTEM-CONFIGURATION',
            legend: 'Configuração de Temas do Sistema',
            route: '/panel/settings/system-config',
          });
        }
      }
      setDynamicSchemes(this.schemesList());
      filterSchemes(this.schemesList());
      this.setMenu(permissions);
    }
  }
}
